<template lang="pug">
div
    //- p {{ availableDates }}
    v-autocomplete(
        v-model="dateSelected"
        :items="availableDates"
        outlined
        dense
    )
</template>

<script>
import { paginator } from "@/utils/paginator";
import { apolloClient } from "@/services/vue-apollo";
import { groupBy } from "lodash";
import moment from "moment";
import { PirManageMode } from "@/utils/misc";

export default {
  props: {
    documentToFetchDates: {
      type: Object,
      required: true,
    },
    queryToReadData: {
      type: String,
      required: true,
    },
    variablesToFetchDates: {
      type: Object,
      default: () => ({}),
    },
    currentItem: {
      type: Object || null,
      default: null,
    },
  },
  data() {
    return {
      canEnterNewValues: true,
      dateSelected: null,
      itemsGroupedByDate: {},
      isFirstBatchAfterVariableChange: true,
    };
  },
  computed: {
    availableDates() {
      return Object.keys(this.itemsGroupedByDate).sort(
        (a, b) => new Date(b) - new Date(a)
      );
    },
  },
  methods: {
    /**
     * Decide PIR mode and whether new values can be entered,
     * then emit both flags to the parent.
     */
    setPirManageMode() {
      // Normalize inputs
      const rawSelected = this.dateSelected;
      const rawFirst = this.availableDates?.[0] || null;
      const today = moment().startOf("day");
      const selected = rawSelected ? moment(rawSelected).startOf("day") : null;
      const firstDate = rawFirst ? moment(rawFirst).startOf("day") : null;
      // Defaults
      let mode = null;
      let canEnterNewVals = false;
      // 1) No date picked → CREATE + allow entry
      if (!selected) {
        mode = PirManageMode.create;
        canEnterNewVals = true;
        // 2) Picking today → UPDATE + allow entry
      } else if (selected.isSame(today, "day")) {
        mode = PirManageMode.update;
        canEnterNewVals = true;
        // 3) Picking a past date that is *also* the first available date
        //    → CREATE + disallow entry
      } else if (
        selected.isBefore(today, "day") &&
        firstDate?.isSame(selected, "day")
      ) {
        mode = PirManageMode.create;
        canEnterNewVals = false;
        // 4) All other cases (e.g. future dates or past non‑first dates)
        //    → mode stays null + disallow entry
      } else {
        mode = null;
        canEnterNewVals = false;
      }
      // Update component state & emit once
      this.canEnterNewValues = canEnterNewVals;
      this.$emit("update:pirManageMode", mode);
      this.$emit("update:canEnterNewValues", canEnterNewVals);
    },
    setNewHistoryItem(data) {
      // console.log("setNewHistoryItem", data);
      let updated = data?.updated || null;
      if (!updated) return;
      updated = moment.utc(updated).format("YYYY-MM-DD");
      const dateItems = this.itemsGroupedByDate?.[updated] || [];
      this.itemsGroupedByDate = {
        ...(this.itemsGroupedByDate || {}),
        [updated]: [{ node: data }, ...dateItems],
      };
      this.dateSelected =
        Object.keys(this.itemsGroupedByDate).sort(
          (a, b) => new Date(b) - new Date(a)
        )?.[0] || null;
      this.setDateAndItsItems();
    },
    setDateAndItsItems() {
      const data = !this.dateSelected
        ? null
        : this.itemsGroupedByDate?.[this.dateSelected]?.[0]?.node || null;
      this.$emit("update:currentItem", data);
      this.setPirManageMode();
    },
    async fetchavailableDates() {
      if (!this.documentToFetchDates || !this.queryToReadData) return;
      let hasNextPage = true;
      let afterCursor = null;
      this.itemsGroupedByDate = {};
      try {
        this.$store.commit("queryLoading", true);
        while (hasNextPage) {
          const { data } = await apolloClient.query({
            query: this.documentToFetchDates,
            fetchPolicy: "network-only", // ensure fresh data each call
            variables: {
              ...this.variablesToFetchDates,
              paging: paginator(10, afterCursor),
            },
          });
          const item = data?.[this.queryToReadData] || {};
          const items = item.edges || [];
          const groupedByDates = groupBy(items, (item) =>
            moment.utc(item.node.updated).format("YYYY-MM-DD")
          );
          for (const key in groupedByDates) {
            const prevItems = this.itemsGroupedByDate?.[key] || [];
            this.itemsGroupedByDate = {
              ...(this.itemsGroupedByDate || {}),
              [key]: prevItems.concat(groupedByDates[key]),
            };
          }
          if (this.isFirstBatchAfterVariableChange) {
            this.dateSelected =
              Object.keys(this.itemsGroupedByDate)?.[0] || null;
            this.setDateAndItsItems();
            this.isFirstBatchAfterVariableChange = false;
          }
          // Prepare for next loop
          hasNextPage = item.pageInfo.hasNextPage;
          afterCursor = item.pageInfo.endCursor;
        }
      } catch (err) {
        //
      } finally {
        this.$store.commit("queryLoading", false);
      }
    },
  },
  mounted() {
    this.$emit("setNewHistoryItem", this.setNewHistoryItem);
    this.$emit("fetchavailableDates", this.fetchavailableDates);
  },
  watch: {
    variablesToFetchDates: {
      deep: true,
      immediate: true,
      handler() {
        this.isFirstBatchAfterVariableChange = true;
        this.fetchavailableDates();
      },
    },
    dateSelected: {
      deep: true,
      handler() {
        if (!this.isFirstBatchAfterVariableChange) {
          this.setDateAndItsItems();
        }
      },
    },
  },
};
</script>
